import { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { Button } from '@uikit/components/Button';
import { ExternalLink } from '@uikit/components/ExternalLink';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import { ItemsProps, ProjectContentTableOfContentTab } from '../types';
import {
  businessModel,
  gallery,
  investors,
  keyFeaturesPart1,
  keyFeaturesPart2,
  partners,
  roadmap,
  teamMembers,
  tokenUtility,
} from './constants';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
  withNumericSymbols?: boolean;
}

const List = ({
  items,
  description,
  className,
  descriptionClassName,
  withNumericSymbols,
}: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul
        className={classNames(
          'pl-6',
          {
            'list-decimal': withNumericSymbols,
            'list-disc': !withNumericSymbols,
          },
          className,
        )}
      >
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id} className="mb-4">
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          GPT Wars is a revolutionary blockchain-powered AAA shooter game designed with Unreal
          Engine 5.4.3, delivering an unparalleled gaming experience. The game combines high-quality
          graphics and fast-paced action with innovative blockchain integrations, including a
          deflationary token model using $GPTW. Players can engage in competitive gameplay, earn
          rewards through a unique “Tap-to-Earn” mechanic, and trade assets in a seamless
          marketplace. By leveraging SEI blockchain technology, GPT Wars ensures scalability and
          efficiency, making it a flagship project in blockchain gaming. The ecosystem also includes
          a Telegram MiniApp for enhanced accessibility and a dynamic marketplace for trading
          in-game assets like skins, loot boxes, and upgrades.
        </p>
        <div className="overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt=""
                  width={640}
                  height={332}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: (
      <>
        <List items={keyFeaturesPart1} />
        <p className="font-semibold py-4"> Traction / Achievements</p>
        <List items={keyFeaturesPart2} />
      </>
    ),
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-1.5">
          {investors.map(({ key, image, width, height, className }) => {
            return (
              <li
                key={key}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={key}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'partners',
    title: 'Partners',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-1.5">
          {partners.map(({ key, image, width, height, className }) => {
            return (
              <li
                key={key}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={key}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: (
      <>
        <p className="mb-3 font-normal max-sm:text-sm">
          GPT Wars employs a robust, multi-revenue model designed to sustain the ecosystem and
          create long-term value for players and investors:
        </p>
        <List items={businessModel} />
      </>
    ),
  },
  {
    id: 'token-utility',
    title: 'Tokenomics and Token Utility',
    content: (
      <>
        <List items={tokenUtility} className="mb-6" />
        <div className="w-full flex justify-center items-center rounded-lg border border-primary-200 mb-6">
          <ImageProxy
            alt="GPTW Allocation Breakdown"
            src="/launchpad/stories/gpt-wars/allocation-breakdown.png"
            className="block w-full h-auto rounded-lg"
            quality={100}
            width={713}
            height={441}
          />
        </div>
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink
            className="underline"
            href="https://docs.google.com/spreadsheets/d/1NfIPeJSwlsX0X7FX4MmSoasqrDW-Spu7/edit?gid=1221192805#gid=1221192805"
          >
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <TeamMemberList
          className="!grid-cols-1 !items-start max-md:flex-col"
          cardClassName="!items-start max-md:flex-col"
          teamMembers={teamMembers}
        />
      </>
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: (
      <>
        <List className="!pl-0 list-none" items={roadmap} />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink
            className="underline"
            href="https://whitepaper.gptwars.ai/resources/roadmap"
          >
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
];
